<template>
  <div class="payment-success-page">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-lg-8 col-xl-6">
          <div class="payment-card">
            <!-- Loading: waiting for the reservation to be created by the webhook -->
            <div v-if="state === 'loading'" class="payment-header text-center">
              <div class="spinner-border text-success mb-3" role="status">
                <span class="sr-only">{{ $t('payment.loading') }}</span>
              </div>
              <h3 class="payment-title">{{ $t('payment.confirmingReservation') }}</h3>
            </div>

            <!-- Success: reservation confirmed -->
            <div v-else-if="state === 'success'" class="payment-header text-center">
              <div class="success-icon mb-3">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#10b981" stroke-width="2" fill="#d1fae5"/>
                  <path d="M8 12l2 2 4-4" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 class="payment-title">{{ $t('payment.success') }}</h3>
              <div class="alert alert-success mt-3" role="alert">
                {{ $t('payment.reservationCreated') }}
              </div>
              <p class="mb-3">{{ $t('payment.redirectingToHome') }}</p>
            </div>

            <!-- Pending: payment received but reservation not visible yet -->
            <div v-else-if="state === 'pending'" class="payment-header text-center">
              <div class="success-icon mb-3">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#10b981" stroke-width="2" fill="#d1fae5"/>
                  <path d="M8 12l2 2 4-4" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 class="payment-title">{{ $t('payment.success') }}</h3>
              <div class="alert alert-success mt-3" role="alert">
                {{ $t('payment.paymentReceivedPending') }}
              </div>
              <button class="btn btn-secondary" @click="$router.push('/')">
                {{ $t('payment.backToHome') }}
              </button>
            </div>

            <!-- Error -->
            <div v-else class="alert alert-danger">
              {{ error }}
              <div class="mt-3">
                <button class="btn btn-secondary" @click="$router.push('/experiencias')">
                  {{ $t('payment.backToExperiences') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_HELPER } from '@/helpers/api.js';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 18;

export default {
  name: 'PaymentSuccess',
  data() {
    return {
      state: 'loading', // loading | success | pending | error
      error: null,
      pollAttempt: 0,
      pollTimer: null,
      seenPaid: false
    };
  },
  mounted() {
    const sessionId = this.$route.query.session_id;

    if (!sessionId) {
      this.state = 'error';
      this.error = this.$t('payment.noSessionId');
      return;
    }

    this.checkCheckoutStatus(sessionId);
  },
  beforeDestroy() {
    clearTimeout(this.pollTimer);
  },
  methods: {
    async checkCheckoutStatus(sessionId) {
      this.pollAttempt++;
      let data = null;

      try {
        const url = API_HELPER.pathJoin([process.env.VUE_APP_API_ROOT, 'Payments', 'confirm-checkout']);
        const requestOptions = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sessionId: sessionId
          })
        };

        data = await API_HELPER.apiCall(url, null, requestOptions);
      } catch (error) {
        // Transient failures count as an attempt; polling continues until the limit
        console.error('Error checking checkout status:', error);
      }

      if (data) {
        if (data.paymentStatus && data.paymentStatus !== 'paid') {
          this.state = 'error';
          this.error = this.$t('payment.paymentNotCompleted');
          return;
        }

        if (data.paymentStatus === 'paid' && !this.seenPaid) {
          this.seenPaid = true;
          // Payment confirmed — clear pending data so another checkout session isn't created
          this.$store.dispatch('payment/clearPendingReservation');
        }

        if (data.reservationCreated) {
          this.state = 'success';
          this.pollTimer = setTimeout(() => {
            this.$router.push('/');
          }, 2000);
          return;
        }
      }

      if (this.pollAttempt >= MAX_POLL_ATTEMPTS) {
        if (this.seenPaid) {
          // Payment went through; the webhook will finish creating the reservation
          this.state = 'pending';
        } else {
          this.state = 'error';
          this.error = this.$t('payment.confirmError');
        }
        return;
      }

      this.pollTimer = setTimeout(() => this.checkCheckoutStatus(sessionId), POLL_INTERVAL_MS);
    }
  }
};
</script>

<style scoped>
.payment-success-page {
  padding: 3rem 0;
  min-height: 60vh;
}

.payment-card {
  background: #ffffff;
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
}

.payment-header {
  margin-bottom: 2rem;
}

.success-icon {
  display: flex;
  justify-content: center;
}

.payment-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--secondary-color);
  margin: 0;
}

.btn-primary {
  background: linear-gradient(135deg, var(--primary-color) 0%, #9fc48a 100%);
  border: none;
  padding: 1rem 2rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(177, 209, 160, 0.4);
}

@media (max-width: 768px) {
  .payment-card {
    padding: 2rem 1.5rem;
  }

  .payment-title {
    font-size: 1.5rem;
  }
}
</style>
